/**
 * 将任意输入安全转成去首尾空格后的字符串。
 */
export function normalizeTextValue(value: unknown): string {
  return String(value ?? '').trim()
}

function clampColorChannel(value: number): string {
  return Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0')
}

/**
 * 归一化 `r,g,b` 文本，确保每个通道都落在 `0-255`。
 */
export function normalizeRgbString(input: unknown): string {
  const raw = normalizeTextValue(input)
  if (!raw) return ''
  const match = raw.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/)
  if (!match) return raw
  const [, r, g, b] = match
  const channels = [r, g, b].map((item) => Math.max(0, Math.min(255, Number(item) || 0)))
  return channels.join(',')
}

/**
 * 将 `r,g,b` 文本转为 `#rrggbb`。
 */
export function rgbStringToHex(input: unknown, fallback = '#ffffff'): string {
  const raw = normalizeTextValue(input)
  if (!raw) return fallback
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase()
  const match = raw.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/)
  if (!match) return fallback
  const [, r, g, b] = match
  return `#${clampColorChannel(Number(r))}${clampColorChannel(Number(g))}${clampColorChannel(Number(b))}`
}

/**
 * 解析显示文本角色名颜色：事件 `RoleColor` 优先，否则与总设对话色 `FontColor_MSG` 一致。
 */
export function resolveDialogRoleColor(
  roleColor: unknown,
  messageColor: unknown,
  fallback = '222,226,241',
): string {
  const fromEvent = normalizeRgbString(roleColor)
  if (fromEvent) return fromEvent
  return normalizeRgbString(messageColor) || fallback
}

/**
 * 将 naive-ui 等外部颜色值（`rgb()` / `#rrggbb` / `r,g,b`）转为 `#rrggbb`。
 */
export function pickerColorToHex(input: unknown, fallback = '#ffffff'): string {
  const raw = normalizeTextValue(input)
  if (!raw) return fallback
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase()
  const cssRgb = raw.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i)
  if (cssRgb) {
    return rgbStringToHex(`${cssRgb[1]},${cssRgb[2]},${cssRgb[3]}`, fallback)
  }
  return rgbStringToHex(raw, fallback)
}

/**
 * 将 `#rrggbb` 转回 `r,g,b` 文本。
 */
export function hexToRgbString(input: unknown, fallback = '255,255,255'): string {
  const raw = normalizeTextValue(input).replace('#', '')
  if (!/^[0-9a-f]{6}$/i.test(raw)) return fallback
  const r = parseInt(raw.slice(0, 2), 16)
  const g = parseInt(raw.slice(2, 4), 16)
  const b = parseInt(raw.slice(4, 6), 16)
  return `${r},${g},${b}`
}
